import { AnimatePresence, motion } from "framer-motion";
import { Check, Info, MessageCircle, ImageIcon } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { CONTACT_PRICE, fileUrl, formatPrice } from "@/lib/api";
import { ServiceSchedule } from "@/components/site/ScheduleInfo";

const TreatmentRow = ({ t, category }) => {
  const { openInquiry } = useSite();
  const price = formatPrice(t);
  return (
    <li className="flex items-center justify-between gap-3 py-3 border-b border-nanina-line last:border-0" data-testid={`detail-treatment-${t.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-nanina-green flex flex-wrap items-center gap-1.5">
          {t.name}
          {t.kind === "package" && <span className="text-[10px] rounded-full bg-nanina-skysoft px-2 py-0.5">Paket</span>}
          {t.promo_label && <span className="text-[10px] rounded-full bg-nanina-butter/70 px-2 py-0.5">{t.promo_label}</span>}
        </p>
        <p className="text-xs text-nanina-muted">{price || CONTACT_PRICE}</p>
      </div>
      <button onClick={() => openInquiry(`${category.name} / ${t.name}`)} className="shrink-0 rounded-full bg-nanina-freshsoft text-nanina-green text-xs font-semibold px-3 py-2 hover:bg-[#D9EFDE] transition-colors" data-testid={`detail-ask-${t.id}`}>
        Tanya
      </button>
    </li>
  );
};

const DocGrid = ({ photos }) => {
  const { openLightbox } = useSite();
  if (!photos.length) {
    return (
      <div className="grid grid-cols-2 gap-2.5 flex-1">
        {["Dokumentasi resmi akan tampil setelah diunggah admin.", "Aktivitas & momen Nanina Care"].map((txt) => (
          <div key={txt} className="rounded-2xl bg-white/60 border border-white min-h-[130px] sm:min-h-[180px] grid place-items-center text-center p-3 text-[11px] text-[#5B877C]">
            <span className="flex flex-col items-center gap-2"><ImageIcon className="h-6 w-6" />{txt}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {photos.slice(0, 4).map((p, i) => (
        <button key={p.id} onClick={() => openLightbox(photos, i)} className="group relative overflow-hidden rounded-2xl aspect-[4/3] bg-white" data-testid={`detail-photo-${p.id}`}>
          <img src={fileUrl(p.id)} alt={p.caption || "Dokumentasi layanan"} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
          {p.caption && <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent text-white text-[10px] text-left p-2 line-clamp-2">{p.caption}</span>}
        </button>
      ))}
    </div>
  );
};

export const ServiceDetail = ({ category }) => {
  const { site, openInquiry } = useSite();
  const items = site.treatments.filter((t) => t.category_id === category.id);
  const photos = site.photos.filter((p) => p.category_id === category.id).sort((a, b) => Number(b.featured) - Number(a.featured));
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={category.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="mt-4 grid lg:grid-cols-[1.1fr_.9fr] overflow-hidden rounded-3xl bg-white border border-nanina-line shadow-[0_18px_54px_rgba(35,75,67,0.07)]"
        data-testid="service-detail"
      >
        <div className="p-5 sm:p-8">
          {category.identity && <span className="inline-block rounded-full bg-nanina-skysoft text-nanina-green text-[11px] font-semibold px-3 py-1" data-testid="service-detail-identity">{category.identity}</span>}
          <h3 className="mt-2 text-2xl sm:text-[28px] font-extrabold tracking-tight text-nanina-green" data-testid="service-detail-name">{category.name}</h3>
          <p className="mt-2 text-sm text-nanina-muted max-w-lg leading-relaxed" data-testid="service-detail-description">{category.description}</p>
          {category.highlights?.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-nanina-fresh">Highlight layanan</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {category.highlights.map((h) => (
                  <span key={h} className="inline-flex items-center gap-1.5 rounded-full bg-nanina-freshsoft text-nanina-green text-xs font-semibold px-3 py-1.5"><Check className="h-3.5 w-3.5" />{h}</span>
                ))}
              </div>
            </div>
          )}
          {items.length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-nanina-fresh">Tindakan & paket</p>
              <ul className="mt-1">{items.map((t) => <TreatmentRow key={t.id} t={t} category={category} />)}</ul>
            </div>
          )}
          {category.info?.length > 0 && (
            <div className="mt-4 rounded-2xl bg-[#EEF7E9] p-3.5 space-y-1.5" data-testid="service-detail-info">
              {category.info.map((i) => (
                <p key={i} className="flex gap-2 text-xs text-nanina-green"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />{i}</p>
              ))}
            </div>
          )}
          <ServiceSchedule categoryId={category.id} />
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button onClick={() => openInquiry(category.name)} className="inline-flex items-center gap-2 rounded-full bg-nanina-green text-white text-sm font-semibold px-5 py-3 hover:bg-nanina-greendark transition-colors" data-testid="service-detail-whatsapp-button">
              <MessageCircle className="h-4 w-4" /> Tanya {category.name} via WhatsApp
            </button>
            <small className="text-[11px] text-nanina-muted">Isi formulir singkat dulu, lalu WhatsApp terbuka.</small>
          </div>
        </div>
        <div className="bg-[linear-gradient(150deg,#E0F1F9,#EBF7EE)] p-4 sm:p-6 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-nanina-green">Dokumentasi layanan</p>
            <a href="#dokumentasi" className="text-[11px] text-nanina-muted hover:text-nanina-green">Lihat galeri</a>
          </div>
          <DocGrid photos={photos} />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
